import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import ReactTable from 'react-table';
import { compose } from 'recompose';
import {
  Button,
} from 'react-bootstrap';

import {
  defaultTableFiltering,
  tableStringSorting,
  openInNewTab,
} from '../../../utils';
import { queryGetAllUnaccountedHoursTicketsDataGQL } from '../graphql/getAllUnaccoutnedHoursTickets';
import { getTicketLink } from '../../ticketViewer/links';


const propTypes = exact({
  allUnaccountedHoursTickets: PropTypes.array.isRequired,

  /* eslint-disable react/no-unused-prop-types */
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  /* eslint-enable react/no-unused-prop-types */
});


const UnaccountedHoursTicketsTable = (props) => {
  const { allUnaccountedHoursTickets } = props;

  const handleSelectTicketObject = (selectedObject) => {
    const ticketId = selectedObject.ticketNumber.ticket.id;
    const jobId = selectedObject.job.id;
    const url = getTicketLink(ticketId, jobId);
    openInNewTab(url);
  };

  return (
    <ReactTable
      className="-striped -highlight"
      filterable
      data={allUnaccountedHoursTickets}
      columns={[
        {
          Header: 'Date',
          id: 'date',
          accessor: (e) => { return e.date; },
          sortMethod: (a, b) => {
            return tableStringSorting(a, b, 'iso8601');
          },
          filterMethod: (filter, row) => {
            const customFilterObj = {
              id: 'display',
              value: filter.value,
            };
            return defaultTableFiltering(customFilterObj, row.date);
          },
          Cell: (row) => {
            return (
              <div>
                { row.original.date.display }
              </div>
            );
          },
        },
        {
          Header: 'Ticket Number',
          id: 'ticketNumber',
          accessor: (e) => {
            return e.ticketNumber.number;
          },
          filterMethod: (filter, row) => {
            const customFilterObj = {
              id: filter.id,
              value: filter.value,
            };
            return defaultTableFiltering(customFilterObj, row);
          },
        },
        {
          Header: 'Job Number',
          id: 'jobNumber',
          accessor: (e) => { return e.job.jobNumber; },
          filterMethod: (filter, row) => {
            const customFilterObj = {
              id: filter.id,
              value: filter.value,
            };
            return defaultTableFiltering(customFilterObj, row);
          },
        },
        {
          Header: 'District',
          id: 'district',
          accessor: (e) => { return e.district; },
          sortMethod: (a, b) => {
            return tableStringSorting(a, b, 'name');
          },
          filterMethod: (filter, row) => {
            const customFilterObj = {
              id: 'name',
              value: filter.value,
            };
            return defaultTableFiltering(customFilterObj, row.district);
          },
          Cell: (row) => {
            return (
              <div>
                { row.original.district.name }
              </div>
            );
          },
        },
        {
          Header: 'Department',
          id: 'department',
          accessor: (e) => { return e.department; },
          sortMethod: (a, b) => {
            return tableStringSorting(a, b, 'name');
          },
          filterMethod: (filter, row) => {
            const customFilterObj = {
              id: 'name',
              value: filter.value,
            };
            return defaultTableFiltering(customFilterObj, row.department);
          },
          Cell: (row) => {
            return (
              <div>
                {row.original.department.name}
              </div>
            );
          },
        },
        {
          Header: 'Operator',
          id: 'operator',
          accessor: (e) => { return e.well.lease.operator; },
          sortMethod: (a, b) => {
            return tableStringSorting(a, b, 'name');
          },
          filterMethod: (filter, row) => {
            const customFilterObj = {
              id: 'name',
              value: filter.value,
            };
            return defaultTableFiltering(customFilterObj, row.operator);
          },
          Cell: (row) => {
            return (
              <div>
                {row.original.well.lease.operator.name}
              </div>
            );
          },
        },
        {
          Header: 'Lease',
          id: 'lease',
          accessor: (e) => { return e.well.lease; },
          sortMethod: (a, b) => {
            return tableStringSorting(a, b, 'name');
          },
          filterMethod: (filter, row) => {
            const customFilterObj = {
              id: 'name',
              value: filter.value,
            };
            return defaultTableFiltering(customFilterObj, row.lease);
          },
          Cell: (row) => {
            return (
              <div>
                {row.original.well.lease.name}
              </div>
            );
          },
        },
        {
          Header: 'Well',
          id: 'well',
          accessor: (e) => { return e.well; },
          sortMethod: (a, b) => {
            return tableStringSorting(a, b, 'name');
          },
          filterMethod: (filter, row) => {
            const customFilterObj = {
              id: 'name',
              value: filter.value,
            };
            return defaultTableFiltering(customFilterObj, row.well);
          },
          Cell: (row) => {
            return (
              <div>
                {row.original.well.name}
              </div>
            );
          },
        },
        {
          Header: 'Billing Hours',
          id: 'billingTime',
          filterable: false,
          accessor: (e) => { return e.billingTime; },
          sortMethod: (a, b) => {
            return tableStringSorting(a, b, 'totalSeconds');
          },
          Cell: (row) => {
            return (
              <div>
                {row.original.billingTime.toString}
              </div>
            );
          },
        },
        {
          Header: 'Average Worked Hours',
          id: 'avgWorkedHours',
          filterable: false,
          accessor: (e) => { return e.avgWorkedHours; },
          sortMethod: (a, b) => {
            return tableStringSorting(a, b, 'totalSeconds');
          },
          Cell: (row) => {
            return (
              <div>
                {row.original.avgWorkedHours.toString}
              </div>
            );
          },
        },
        {
          Header: 'Delta',
          id: 'deltaBilledAndWorkedTime',
          filterable: false,
          accessor: (e) => { return e.deltaBilledAndWorkedTime; },
          sortMethod: (a, b) => {
            return tableStringSorting(a, b, 'totalSeconds');
          },
          Cell: (row) => {
            const detlaTotalSeconds = row.original.deltaBilledAndWorkedTime.totalSeconds;

            const highlightColor = (detlaTotalSeconds < 0) ? 'alert-danger' : '';
            return (
              <div className={highlightColor}>
                {row.original.deltaBilledAndWorkedTime.toString}
              </div>
            );
          },
        },
        {
          Header: 'Actions',
          id: 'actions',
          filterable: false,
          sortable: false,
          Cell: (row) => {
            return (
              <div>
                <Button
                  bsStyle="info"
                  bsSize="xsmall"
                  onClick={() => { handleSelectTicketObject(row.original); }}
                >View
                </Button>
              </div>

            );
          },
        },
      ]}
    />
  );
};

UnaccountedHoursTicketsTable.propTypes = propTypes;

const enhance = compose(queryGetAllUnaccountedHoursTicketsDataGQL);

export default enhance(UnaccountedHoursTicketsTable);
